'use client'
import { Select } from '@/components/ui/select'
import { TCollectionFormatted } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { COLLECTION_SORT_LABELS, ECollectionSort } from '../../constants'
import { useCollectionStore } from '../../store/collection-store'
import {
  applyFilter,
  FilterTag,
  getFilterTags,
  objectToSearchParams,
} from '../../utils/filter-tag-utils'
import { getFilterStatus, updateUrl } from '../../utils/filter-utils'
import { FilterBarTags } from '../filter-bar/filter-bar-tags'
import { processSeoRedirectRemoveTag } from '../filter-bar/use-filter-state'

type Props = {
  collection: TCollectionFormatted
  showTitle?: boolean
}

const hideSorts = ['san-pham-moi']

export const FilterSummaryBar: React.FC<Props> = ({
  collection,
  showTitle,
}) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const setFilter = useCollectionStore((s) => s.setFilter)
  const filter = useCollectionStore((s) => s.filter)
  const router = useRouter()
  const t = useTranslations('collection_page')

  const handleSortChange = (value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('sort', value)
    updateUrl(`${pathname}?${params.toString()}`, { scroll: false })
    setFilter({ sort: value })
  }

  const handleClearFilters = () => {
    const currentParams = objectToSearchParams(filter)
    const newUrl = processSeoRedirectRemoveTag(
      pathname,
      collection,
      new URLSearchParams(),
      currentParams
    )
    const newPath = newUrl.split('?')[0]

    if (window.location.pathname + window.location.search !== newUrl) {
      if (newPath !== window.location.pathname) {
        router.push(newUrl, { scroll: false })
      } else {
        updateUrl(newUrl, { scroll: false })
      }
    }
    setFilter({})
  }

  const tags = getFilterTags(filter, collection)

  const onRemoveTag = (tag: FilterTag) => {
    const newFilters = applyFilter(filter, tag)
    const newParams = objectToSearchParams(newFilters)
    const currentParams = objectToSearchParams(filter)
    setFilter(newFilters)

    const newUrl = processSeoRedirectRemoveTag(
      pathname,
      collection,
      newParams,
      currentParams
    )

    const newPath = newUrl.split('?')[0]

    if (window.location.pathname + window.location.search !== newUrl) {
      if (newPath !== window.location.pathname) {
        router.push(newUrl, { scroll: false })
      } else {
        updateUrl(newUrl, { scroll: false })
      }
    }
  }

  const { hasFilter } = getFilterStatus(filter, collection)

  const sortOptions = Object.entries(COLLECTION_SORT_LABELS).map(
    ([key, label]) => ({
      value: key,
      label: t(`sort_label.${label}`),
    })
  )

  return (
    <>
      {showTitle && hasFilter && (
        <h1 className='mt-6 font-criteria text-3xl font-medium leading-7 text-neutral-900'>
          {collection.title_display}
        </h1>
      )}
      <div className='flex items-center justify-between'>
        <div className='flex basis-8/12 items-center gap-3'>
          {showTitle && !hasFilter && (
            <h1 className='font-criteria text-3xl font-medium leading-7 text-neutral-900'>
              {collection.title_display}
            </h1>
          )}
          <FilterBarTags tags={tags} onRemoveTag={onRemoveTag} />
          {hasFilter && (
            <button
              className='whitespace-nowrap font-sans text-sm font-semibold leading-4.5 text-primary underline-offset-2 hover:underline'
              onClick={handleClearFilters}
            >
              {t('button.clear_filter')}
            </button>
          )}
        </div>
        {!hideSorts.includes(collection.detail?.seo_alias) && (
          <div className='flex items-center gap-2'>
            <span className='whitespace-nowrap font-sans text-sm font-medium uppercase leading-4.5 text-neutral-900 opacity-40'>
              {t('title.sort_by')}
            </span>
            <Select
              options={sortOptions}
              onValueChange={handleSortChange}
              defaultValue={
                searchParams.get('sort') || ECollectionSort.BESTSELLER
              }
              className={{
                trigger: 'border-neutral-100 bg-neutral-100',
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}
